function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
var _intersectionObserver = _classPrivateFieldLooseKey("intersectionObserver");
var _mutationObserver = _classPrivateFieldLooseKey("mutationObserver");
var _cachedRecommendations = _classPrivateFieldLooseKey("cachedRecommendations");
var _activeFetch = _classPrivateFieldLooseKey("activeFetch");
var _loadRecommendations = _classPrivateFieldLooseKey("loadRecommendations");
var _fetchCachedRecommendations = _classPrivateFieldLooseKey("fetchCachedRecommendations");
var _handleError = _classPrivateFieldLooseKey("handleError");
class ProductRecommendations extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Handle errors in a consistent way
     * @param {Error} error
     */
    Object.defineProperty(this, _handleError, {
      value: _handleError2
    });
    /**
     * Fetches the recommendations and cached the result for future use
     * @param {string} productId
     * @param {string | undefined} sectionId
     * @param {string | undefined} intent
     * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
     */
    Object.defineProperty(this, _fetchCachedRecommendations, {
      value: _fetchCachedRecommendations2
    });
    /**
     * Load the product recommendations
     */
    Object.defineProperty(this, _loadRecommendations, {
      value: _loadRecommendations2
    });
    /**
     * The observer for the product recommendations
     * @type {IntersectionObserver}
     */
    Object.defineProperty(this, _intersectionObserver, {
      writable: true,
      value: new IntersectionObserver((entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        _classPrivateFieldLooseBase(this, _loadRecommendations)[_loadRecommendations]();
      }, {
        rootMargin: '0px 0px 400px 0px'
      })
    });
    /**
     * Observing changes to the elements attributes
     * @type {MutationObserver}
     */
    Object.defineProperty(this, _mutationObserver, {
      writable: true,
      value: new MutationObserver(mutations => {
        for (const mutation of mutations) {
          // Only attribute changes are interesting
          if (mutation.target !== this || mutation.type !== 'attributes') continue;

          // Ignore error attribute changes
          if (mutation.attributeName === 'data-error') continue;

          // Ignore addition of hidden class because it means there's an error with the display
          if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

          // Ignore when the data-recommendations-performed attribute has been set to 'true'
          if (mutation.attributeName === 'data-recommendations-performed' && this.dataset.recommendationsPerformed === 'true') continue;

          // All other attribute changes trigger a reload
          _classPrivateFieldLooseBase(this, _loadRecommendations)[_loadRecommendations]();
          break;
        }
      })
    });
    /**
     * The cached recommendations
     * @type {Record<string, string>}
     */
    Object.defineProperty(this, _cachedRecommendations, {
      writable: true,
      value: {}
    });
    /**
     * An abort controller for the active fetch (if there is one)
     * @type {AbortController | null}
     */
    Object.defineProperty(this, _activeFetch, {
      writable: true,
      value: null
    });
  }
  connectedCallback() {
    super.connectedCallback();
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].observe(this);
    _classPrivateFieldLooseBase(this, _mutationObserver)[_mutationObserver].observe(this, {
      attributes: true
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].disconnect();
    _classPrivateFieldLooseBase(this, _mutationObserver)[_mutationObserver].disconnect();
  }
}
function _loadRecommendations2() {
  const {
    productId,
    recommendationsPerformed,
    sectionId,
    intent
  } = this.dataset;
  const id = this.id;
  if (!productId || !id) {
    throw new Error('Product ID and an ID attribute are required');
  }

  // If the recommendations have already been loaded, accounts for the case where the Theme Editor
  // is loaded the section from the editor's visual preview context.
  if (recommendationsPerformed === 'true') {
    return;
  }
  _classPrivateFieldLooseBase(this, _fetchCachedRecommendations)[_fetchCachedRecommendations](productId, sectionId, intent).then(result => {
    if (!result.success) {
      // The Theme Editor will place a section element element in the DOM whose section_id is not available
      // to the Section Renderer API. In this case, we can safely ignore the error.
      if (!Shopify.designMode) {
        _classPrivateFieldLooseBase(this, _handleError)[_handleError](new Error(`Server returned ${result.status}`));
      }
      return;
    }
    const html = document.createElement('div');
    html.innerHTML = result.data || '';
    const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
    if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
      this.dataset.recommendationsPerformed = 'true';
      this.innerHTML = recommendations.innerHTML;
    } else {
      _classPrivateFieldLooseBase(this, _handleError)[_handleError](new Error('No recommendations available'));
    }
  }).catch(e => {
    _classPrivateFieldLooseBase(this, _handleError)[_handleError](e);
  });
}
async function _fetchCachedRecommendations2(productId, sectionId, intent) {
  const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
  const cachedResponse = _classPrivateFieldLooseBase(this, _cachedRecommendations)[_cachedRecommendations][url];
  if (cachedResponse) {
    return {
      success: true,
      data: cachedResponse
    };
  }
  _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch]?.abort();
  _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = new AbortController();
  try {
    const response = await fetch(url, {
      signal: _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch].signal
    });
    if (!response.ok) {
      return {
        success: false,
        status: response.status
      };
    }
    const text = await response.text();
    _classPrivateFieldLooseBase(this, _cachedRecommendations)[_cachedRecommendations][url] = text;
    return {
      success: true,
      data: text
    };
  } finally {
    _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = null;
  }
}
function _handleError2(error) {
  console.error('Product recommendations error:', error.message);
  this.classList.add('hidden');
  this.dataset.error = 'Error loading product recommendations';
}
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
